/** Media vocabulary shared by the processing pipeline, the API and the player. */

export const TRACK_KINDS = ['audio', 'subtitle'] as const;
export type TrackKind = (typeof TRACK_KINDS)[number];

export const TRACK_SOURCES = ['embedded', 'upload'] as const;
export type TrackSource = (typeof TRACK_SOURCES)[number];

export interface TrackKindDefinition {
  /** Hosts may delete tracks of this kind; audio is part of the film itself. */
  readonly deletable: boolean;
  /** Hosts may add tracks of this kind by uploading a file. */
  readonly uploadable: boolean;
}

export const TRACK_KIND_DEFINITIONS = {
  audio: { deletable: false, uploadable: false },
  subtitle: { deletable: true, uploadable: true },
} as const satisfies Record<TrackKind, TrackKindDefinition>;

/** Subtitle files hosts may upload; the worker converts all of them to WebVTT. */
export const SUBTITLE_UPLOAD_FORMATS = {
  srt: { extensions: ['.srt'] },
  vtt: { extensions: ['.vtt'] },
  ass: { extensions: ['.ass', '.ssa'] },
} as const satisfies Record<string, { readonly extensions: readonly string[] }>;
export type SubtitleUploadFormat = keyof typeof SUBTITLE_UPLOAD_FORMATS;

export const SUBTITLE_UPLOAD_EXTENSIONS: readonly string[] = Object.values(SUBTITLE_UPLOAD_FORMATS).flatMap(
  (format) => format.extensions,
);

/** Sources a participant can publish into the call. Screen share is deliberately absent. */
export const CALL_SOURCES = ['microphone', 'camera'] as const;
export type CallSource = (typeof CALL_SOURCES)[number];

/** Tus upload metadata keys understood by the server. */
export const UPLOAD_METADATA_KEYS = { assetId: 'assetId', filename: 'filename' } as const;

/** Where an asset's poster comes from. A frame is taken automatically; hosts may upload their own. */
export const POSTER_SOURCES = ['frame', 'upload'] as const;
export type PosterSource = (typeof POSTER_SOURCES)[number];

export interface PosterSourceDefinition {
  /** Reprocessing the film replaces the poster with a new frame. */
  readonly replacedOnReprocess: boolean;
  /** Hosts can return to the automatic frame. */
  readonly resettable: boolean;
}

export const POSTER_SOURCE_DEFINITIONS = {
  frame: { replacedOnReprocess: true, resettable: false },
  upload: { replacedOnReprocess: false, resettable: true },
} as const satisfies Record<PosterSource, PosterSourceDefinition>;

/** Images hosts may upload as a poster (recognized by content); the API converts each to a web JPEG. */
export const POSTER_UPLOAD_FORMATS = {
  jpeg: { extensions: ['.jpg', '.jpeg'], mimeTypes: ['image/jpeg'] },
  png: { extensions: ['.png'], mimeTypes: ['image/png'] },
  webp: { extensions: ['.webp'], mimeTypes: ['image/webp'] },
} as const satisfies Record<
  string,
  { readonly extensions: readonly string[]; readonly mimeTypes: readonly string[] }
>;
export type PosterUploadFormat = keyof typeof POSTER_UPLOAD_FORMATS;

export const POSTER_UPLOAD_EXTENSIONS: readonly string[] = Object.values(POSTER_UPLOAD_FORMATS).flatMap(
  (format) => format.extensions,
);
export const POSTER_UPLOAD_MIME_TYPES: readonly string[] = Object.values(POSTER_UPLOAD_FORMATS).flatMap(
  (format) => format.mimeTypes,
);
